<script setup lang="ts">
import { ref } from 'vue';
import type { EntityCategory, EntityDocument, EntityTemplate } from '../types/entities';
import { blankField, fromTemplateField, serializeBuilderFields } from '../lib/fieldBuilder';
import FieldBuilder from './FieldBuilder.vue';

const props = defineProps<{ entity: EntityDocument; categories: EntityCategory[]; templates: EntityTemplate[] }>();
const emit = defineEmits<{
  close: [];
  save: [entityId: string, input: { fields: Array<Record<string, unknown>>; baseRevision: number }, done: (error?: string) => void];
}>();
const baseRevision = props.entity.workingVersion.revision;
const fields = ref(props.entity.entityFields?.length ? props.entity.entityFields.map(fromTemplateField) : [blankField()]);
const busy = ref(false);
const error = ref('');
function updateKey(index: number) {
  const field = fields.value[index];
  if (field && !field.key) field.key = field.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 80);
}
function save() {
  if (fields.value.some((field) => !field.label.trim() || !/^[a-z][a-zA-Z0-9_]{1,79}$/.test(field.key))) {
    error.value = 'Jedes Feld benötigt eine Bezeichnung und einen eindeutigen Schlüssel aus 2 bis 80 Zeichen, beginnend mit einem Kleinbuchstaben.';
    return;
  }
  busy.value = true; error.value = '';
  emit('save', props.entity.id, { fields: serializeBuilderFields(fields.value), baseRevision }, (message) => {
    busy.value = false;
    if (message) error.value = message;
    else emit('close');
  });
}
</script>

<template>
  <Teleport to="body">
    <div class="structure-dialog-backdrop entity-field-backdrop" @click.self="!busy && emit('close')" @keydown.esc="!busy && emit('close')">
      <form class="builder-card template-builder structure-dialog entity-field-dialog" role="dialog" aria-modal="true" aria-labelledby="entity-field-title" @submit.prevent="save">
        <header><div><small>{{ entity.title }}</small><h2 id="entity-field-title">Eigene Felder</h2></div><button type="button" aria-label="Schließen" :disabled="busy" @click="emit('close')">×</button></header>
        <p class="form-hint">Diese Felder gehören nur zu dieser Entity. „Zeitlich tracken“ lässt ihre Werte mit dem globalen Chronis-Zeitkontext wechseln.</p>
        <fieldset :disabled="busy">
          <div class="field-builder-heading"><div><strong>Strukturierte Felder</strong><span>Referenzfelder können auf Kategorien oder exakte Templates begrenzt werden.</span></div><button type="button" class="ghost-button" data-testid="add-field" :disabled="fields.length >= 80" @click="fields.push(blankField())">＋ Feld</button></div>
          <FieldBuilder :fields="fields" :categories="categories" :templates="templates" @update-key="updateKey" @remove="fields.splice($event, 1)" />
        </fieldset>
        <p class="form-hint">Umbenennungen übernehmen gespeicherte Werte und Zustände. Ungültige Werte und entfernte Felder werden auch aus früheren Fassungen und dem Feldverlauf gelöscht. Wird „Zeitlich tracken“ ausgeschaltet, entfallen die zeitlichen Zustände; der Basiswert bleibt erhalten.</p>
        <p v-if="error" class="dialog-error" role="alert">{{ error }}</p>
        <div class="builder-actions sticky-actions"><button type="button" class="ghost-button" :disabled="busy" @click="emit('close')">Abbrechen</button><button type="submit" data-testid="save-entity-fields" :disabled="busy">{{ busy ? 'Speichert …' : 'Änderungen speichern' }}</button></div>
      </form>
    </div>
  </Teleport>
</template>

<style scoped>
.entity-field-backdrop { z-index: 500; padding: 20px; }
.entity-field-dialog { box-sizing: border-box; margin: 0; width: min(1120px, calc(100vw - 40px)); max-height: calc(100vh - 40px); padding: 0 22px 22px; overflow-y: auto; }
header { position: sticky; top: 0; z-index: 2; display: flex; justify-content: space-between; align-items: center; gap: 16px; margin: 0 -22px 20px; padding: 18px 22px; border-bottom: 1px solid var(--ui-border); background: var(--ui-surface-2); }
header small { color: var(--ui-text-muted); } h2 { margin: 4px 0 0; font-size: 20px; }
header button { width: 34px; padding: 0; font-size: 22px; }
fieldset { margin: 0; padding: 0; border: 0; min-width: 0; }
.sticky-actions { margin-bottom: -22px; }
@media(max-width:760px) { .entity-field-backdrop { padding: 10px; } .entity-field-dialog { width: calc(100vw - 20px); max-height: calc(100vh - 20px); } }
</style>
